/** What happened, what the service wrote down, and the decisions people took.
 *
 * Three records, one screen, because they answer the same question from
 * three sides. The activity feed is what happened, in order — the job ran,
 * a survey arrived, it was held, an issue was raised and resolved, a pull
 * request merged — read out of the tables that already hold each of those,
 * and it is the record to read first. The logs are what the service noticed
 * — an upload refused, a token that failed, a stream throttled — and the
 * audit trail is what a person did about it, document by document. Somebody
 * chasing "why is this contributor's upload held" reads the feed, then the
 * other two, and switching tabs should not mean switching screens.
 *
 * Both are read as a table with the interesting cell in the middle, and both
 * open a row to show what the one line could not: the log's field bag, and
 * the audit row's before and after. The audit sentence is written per action
 * in the portal (`components/admin/audit.ts`) rather than sent by the
 * service, which has no business phrasing its own history; an action this
 * build has never heard of still gets a sentence made from whatever changed.
 *
 * The level filter and the search box are the same shape as the datalogger's
 * own log viewer, deliberately: an operator who has used one has used both.
 * Logs are swept at `logs.retention_days`; the audit table is never swept,
 * because it is the record of who decided what.
 *
 * Above the service's lines sits the one picture on this screen: track
 * uploads over time, submitted and queued, with the refusals beneath them.
 * It is here rather than on the Dashboard because it is what the lines are
 * usually about — a column that looks wrong is a reason to read the log for
 * that hour, and the log is right there.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { adminAudit, adminLogs } from '@/lib/api';
import { dateTime } from '@/lib/format';
import type { AdminAuditEntry, AdminAuditPage, AdminLogPage, LogLevel, LogRow } from '@/lib/types';
import {
  EmptyState,
  ErrorNote,
  Field,
  LoadingPanel,
  Page,
  Segmented,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
} from '@/components/ui';
import {
  ActivityFeed,
  AdminHeader,
  KeyValueGrid,
  LoadMore,
  UploadTimelineCard,
  actorLabel,
  auditSummary,
  changedKeys,
  formatValue,
  isActivityKind,
  targetLabel,
  usePaged,
  useQueryParams,
} from '@/components/admin';
import type { SetParams } from '@/components/admin';
import type { ActivityKind } from '@/lib/types';

type View = 'activity' | 'logs' | 'audit';

function viewOf(raw: string | null): View {
  return raw === 'logs' || raw === 'audit' ? raw : 'activity';
}
type LevelFilter = LogLevel | 'all';

const LEVELS: readonly LogLevel[] = ['debug', 'info', 'warn', 'error'];

function isLevel(value: string): value is LogLevel {
  return (LEVELS as readonly string[]).includes(value);
}

/** The level's hue: the two that want a look are the two warm ones, and
 *  debug recedes. */
function levelColour(level: LogLevel): string {
  switch (level) {
    case 'error':
      return 'var(--color-error)';
    case 'warn':
      return 'var(--color-warn)';
    case 'info':
      return 'var(--color-neutral-300)';
    case 'debug':
      return 'var(--color-neutral-600)';
  }
}

/** `upload_id=upl_8f2  from=held  to=pending` — the first few fields, for the row. */
function fieldsPreview(fields: Record<string, unknown>, limit = 4): string {
  const entries = Object.entries(fields);
  const shown = entries.slice(0, limit).map(([key, value]) => `${key}=${formatValue(value)}`);
  const more = entries.length > limit ? ` +${entries.length - limit}` : '';
  return shown.join('  ') + more;
}

const MONO = { fontFamily: 'var(--font-mono)', fontSize: 12 } as const;

const PRE = {
  margin: 0,
  padding: '8px 10px',
  fontSize: 11.5,
  lineHeight: 1.5,
  background: 'var(--color-bg)',
  borderRadius: 'var(--radius-md)',
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
} as const;

interface PanelProps {
  search: URLSearchParams;
  setParam: SetParams;
}

export default function AdminLogs() {
  const [search, setParam] = useQueryParams();
  const view = viewOf(search.get('view'));

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader>
        <Segmented<View>
          label="which record"
          value={view}
          options={[
            { value: 'activity', label: 'What happened' },
            { value: 'logs', label: 'Service logs' },
            { value: 'audit', label: 'Audit trail' },
          ]}
          onChange={(next) =>
            // Each record has its own filters; a switch starts clean.
            setParam({
              view: next === 'activity' ? null : next,
              level: null,
              q: null,
              logger: null,
              action: null,
              target_id: null,
              kind: null,
            })
          }
        />
      </AdminHeader>

      {view === 'audit' ? (
        <AuditPanel search={search} setParam={setParam} />
      ) : view === 'logs' ? (
        <LogsPanel search={search} setParam={setParam} />
      ) : (
        <ActivityPanel search={search} setParam={setParam} />
      )}
    </Page>
  );
}

// ── what happened ─────────────────────────────────────────────────────────

/** The kinds switched on live in the address, so a filtered feed can be
 *  handed to somebody. */
export function kindsOf(search: URLSearchParams): ActivityKind[] {
  return (search.get('kind') ?? '')
    .split(',')
    .map((kind) => kind.trim())
    .filter(isActivityKind);
}

function ActivityPanel({ search, setParam }: PanelProps) {
  const kinds = kindsOf(search);
  return (
    <section aria-label="What happened" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-neutral-500)', maxWidth: 860, textWrap: 'pretty' }}>
        Every run of the merge job, every survey that arrived and what was decided about it, every pull
        request opened and settled, every issue raised and answered, every circuit published. Read from
        the tables that keep each of those, so it reaches back as far as they do; a circuit’s name opens
        its own history.
      </p>
      <ActivityFeed
        kinds={kinds}
        onKindsChange={(next) => setParam({ kind: next.length > 0 ? next.join(',') : null })}
      />
    </section>
  );
}

// ── the service's own lines ───────────────────────────────────────────────

function LogsPanel({ search, setParam }: PanelProps) {
  const rawLevel = search.get('level') ?? 'all';
  const level: LevelFilter = isLevel(rawLevel) ? rawLevel : 'all';
  const q = search.get('q') ?? '';
  const logger = search.get('logger') ?? '';
  const narrowed = Boolean(q || logger) || level !== 'all';

  const [draft, setDraft] = useState({ q, logger });
  useEffect(() => setDraft({ q, logger }), [q, logger]);

  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminLogs({
        level: level === 'all' ? undefined : level,
        q: q || undefined,
        logger: logger || undefined,
        cursor,
        limit: 100,
      }),
    [level, q, logger],
  );
  const listing = usePaged<LogRow, AdminLogPage>(fetcher);
  const [open, setOpen] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setParam({ q: draft.q.trim(), logger: draft.logger.trim() });
  };

  return (
    <section aria-label="Service logs" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* The shape first, the lines that explain it underneath. */}
      <UploadTimelineCard />
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <Segmented<LevelFilter>
          label="filter by level"
          value={level}
          options={[{ value: 'all', label: 'All' }, ...LEVELS.map((value) => ({ value, label: value }))]}
          onChange={(next) => setParam({ level: next === 'all' ? null : next })}
        />
        <form
          onSubmit={submit}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto' }}
        >
          <TextField
            label="search"
            placeholder="message or field — an id, a track, a status"
            value={draft.q}
            onChange={(event) => setDraft({ ...draft, q: event.currentTarget.value })}
            style={{ minHeight: 30, fontSize: 12.5, width: 260 }}
          />
          <TextField
            label="logger"
            placeholder="auth, admin, request…"
            value={draft.logger}
            onChange={(event) => setDraft({ ...draft, logger: event.currentTarget.value })}
            style={{ minHeight: 30, fontSize: 12.5, width: 140, fontFamily: 'var(--font-mono)' }}
          />
          <button type="submit" className="btn btn-secondary" style={{ fontSize: 12 }}>
            Filter
          </button>
          {q || logger ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
              onClick={() => setParam({ q: null, logger: null })}
            >
              Clear
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={listing.reload}>
            Refresh
          </button>
        </form>
      </div>

      {listing.error ? (
        <ErrorNote error={listing.error} title="the logs could not be read" onRetry={listing.reload} />
      ) : null}

      {listing.loading ? (
        <LoadingPanel label="loading logs" />
      ) : listing.items.length === 0 ? (
        <EmptyState title={narrowed ? 'nothing matches' : 'nothing logged yet'}>
          {narrowed
            ? 'Widen the level, or clear the search.'
            : 'Every request writes a line; the first arrives with the first visit. Lines are swept after the retention in Settings.'}
        </EmptyState>
      ) : (
        <Table scroll label="Service logs" style={{ fontSize: 13 }}>
          <THead>
            <TR>
              <TH>Time</TH>
              <TH>Level</TH>
              <TH>Logger</TH>
              <TH>Message</TH>
              <TH>Fields</TH>
            </TR>
          </THead>
          <TBody>
            {listing.items.map((row) => {
              const expanded = open === row.id;
              const fields = row.fields ?? {};
              return (
                <Fragment key={row.id}>
                  <TR selected={expanded} onSelect={() => setOpen(expanded ? null : row.id)}>
                    <TD nowrap muted className="tabular-nums">
                      {dateTime(row.at)}
                    </TD>
                    <TD nowrap color={levelColour(row.level)} style={{ fontSize: 12 }}>
                      {row.level}
                    </TD>
                    <TD nowrap mono muted>
                      {row.logger}
                    </TD>
                    <TD>{row.message}</TD>
                    <TD
                      mono
                      muted
                      title={Object.keys(fields).length > 0 ? 'open the row for every field' : undefined}
                      style={{
                        maxWidth: 420,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 11,
                      }}
                    >
                      {fieldsPreview(fields)}
                    </TD>
                  </TR>
                  {expanded ? (
                    <TR>
                      <TD colSpan={5}>
                        <pre style={PRE}>{JSON.stringify(fields, null, 2)}</pre>
                      </TD>
                    </TR>
                  ) : null}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}

      <LoadMore listing={listing} />
    </section>
  );
}

// ── what people decided ───────────────────────────────────────────────────

function AuditPanel({ search, setParam }: PanelProps) {
  const action = search.get('action') ?? '';
  const q = search.get('q') ?? '';
  const targetId = search.get('target_id') ?? '';
  const narrowed = Boolean(action || q || targetId);

  const [draft, setDraft] = useState({ q, target_id: targetId });
  useEffect(() => setDraft({ q, target_id: targetId }), [q, targetId]);

  const fetcher = useCallback(
    (cursor: string | undefined) =>
      adminAudit({
        action: action || undefined,
        q: q || undefined,
        target_id: targetId || undefined,
        cursor,
        limit: 100,
      }),
    [action, q, targetId],
  );
  const listing = usePaged<AdminAuditEntry, AdminAuditPage>(fetcher);
  const [open, setOpen] = useState<string | null>(null);

  // The action list is the deployment's own history; a filter that matches
  // nothing is still offered, so the option the address bar named stays.
  const actions = listing.page?.actions ?? [];
  const options = action && !actions.includes(action) ? [action, ...actions] : actions;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setParam({ q: draft.q.trim(), target_id: draft.target_id.trim() });
  };

  return (
    <section aria-label="Audit trail" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <Field label="action">
          {({ id }) => (
            <select
              id={id}
              className="input"
              value={action}
              style={{ ...MONO, minHeight: 30, width: 220 }}
              onChange={(event) => setParam({ action: event.currentTarget.value || null })}
            >
              <option value="">every action</option>
              {options.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <form
          onSubmit={submit}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginLeft: 'auto' }}
        >
          <TextField
            label="target"
            placeholder="upl_…, usr_…, iss_…"
            value={draft.target_id}
            onChange={(event) => setDraft({ ...draft, target_id: event.currentTarget.value })}
            style={{ minHeight: 30, fontSize: 12.5, width: 170, fontFamily: 'var(--font-mono)' }}
          />
          <TextField
            label="search"
            placeholder="anything in the documents"
            value={draft.q}
            onChange={(event) => setDraft({ ...draft, q: event.currentTarget.value })}
            style={{ minHeight: 30, fontSize: 12.5, width: 220 }}
          />
          <button type="submit" className="btn btn-secondary" style={{ fontSize: 12 }}>
            Filter
          </button>
          {q || targetId ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
              onClick={() => setParam({ q: null, target_id: null })}
            >
              Clear
            </button>
          ) : null}
          <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={listing.reload}>
            Refresh
          </button>
        </form>
      </div>

      {listing.error ? (
        <ErrorNote error={listing.error} title="the audit trail could not be read" onRetry={listing.reload} />
      ) : null}

      {listing.loading ? (
        <LoadingPanel label="loading the audit trail" />
      ) : listing.items.length === 0 ? (
        <EmptyState title={narrowed ? 'nothing matches' : 'nothing decided yet'}>
          {narrowed
            ? 'Pick another action, or clear the search.'
            : 'Every admin action writes a row here — a moved upload, a disabled account, a changed setting — and none is ever swept.'}
        </EmptyState>
      ) : (
        <Table scroll label="Audit trail" style={{ fontSize: 13 }}>
          <THead>
            <TR>
              <TH>When</TH>
              <TH>Who</TH>
              <TH>Action</TH>
              <TH>Target</TH>
              <TH>What changed</TH>
            </TR>
          </THead>
          <TBody>
            {listing.items.map((entry) => {
              const expanded = open === entry.id;
              const rows = expanded ? changedKeys(entry.before, entry.after) : [];
              return (
                <Fragment key={entry.id}>
                  <TR selected={expanded} onSelect={() => setOpen(expanded ? null : entry.id)}>
                    <TD nowrap muted className="tabular-nums">
                      {dateTime(entry.at)}
                    </TD>
                    <TD nowrap>{actorLabel(entry)}</TD>
                    <TD nowrap mono>
                      {entry.action}
                    </TD>
                    <TD nowrap mono muted title={entry.target_id || undefined}>
                      {targetLabel(entry)}
                    </TD>
                    <TD style={{ textWrap: 'pretty' }}>{auditSummary(entry)}</TD>
                  </TR>
                  {expanded ? (
                    <TR>
                      <TD colSpan={5}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '2px 0 8px' }}>
                          {rows.length > 0 ? (
                            <KeyValueGrid
                              label="what changed"
                              min={180}
                              items={rows.map((row) => ({
                                key: row.key,
                                value: (
                                  <span>
                                    <span style={{ color: 'var(--color-neutral-500)' }}>{row.before}</span>
                                    {' → '}
                                    {row.after}
                                  </span>
                                ),
                              }))}
                            />
                          ) : (
                            <span style={{ fontSize: 12.5, color: 'var(--color-neutral-500)' }}>
                              nothing differs between before and after
                            </span>
                          )}
                          <details>
                            <summary style={{ fontSize: 11.5, color: 'var(--color-neutral-500)', cursor: 'pointer' }}>
                              the documents as recorded
                            </summary>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                gap: 8,
                                marginTop: 8,
                              }}
                            >
                              <pre style={PRE}>{`before\n${JSON.stringify(entry.before, null, 2)}`}</pre>
                              <pre style={PRE}>{`after\n${JSON.stringify(entry.after, null, 2)}`}</pre>
                            </div>
                          </details>
                        </div>
                      </TD>
                    </TR>
                  ) : null}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
      )}

      <LoadMore listing={listing} />
    </section>
  );
}
